import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import ShowAndEditContent from "./ShowEditContent";

const RenderRecordsCell = ({
  tooltipConfig,
  record,
  accessor,
  dataType,
  UIComponent,
  WrapperComponent,
  formatFunction,
  lines,
  rest,
}) => {
  const hoverTimeoutRef = useRef(null);
  let newRecord = record;
  const [showPortal, setShowPortal] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const cellRef = useRef(null);

  if (formatFunction) newRecord[accessor] = formatFunction(record[accessor]);

  useEffect(() => {
    if (!cellRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          setShowPortal(false);
          clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
        }
      },
      { root: null, rootMargin: "-200px 0px -200px 0px", threshold: 0 },
    );

    observer.observe(cellRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!cellRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          setShowPortal(false);
          clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
        }
      },
      { root: null, rootMargin: "-300px 0px -300px 0px", threshold: 0 },
    );

    observer.observe(cellRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!cellRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          setShowPortal(false);
          clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
        }
      },
      { root: null, rootMargin: "-400px 0px -400px 0px", threshold: 0 },
    );

    observer.observe(cellRef.current);

    return () => observer.disconnect();
  }, []);

  // this is a common handling for the managed devices to disable tooltip when any menu is open as the tooltip was getting opened on hover of the table cell even when the user was trying to click on the menu item and it was creating a bad user experience. we can remove this handling once the AGENT of MAC has completed the tools menu as this issue is only observed in the AGENT of MAC because of the way the menu is designed in that.
  // ownership: roy

  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (
        document.body?.getAttribute("data-disable-table-tooltip") === "true"
      ) {
        setShowPortal(false); // or however you hide the tooltip
      }
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-disable-table-tooltip"],
    });

    return () => observer.disconnect();
  }, []);

  const handleMouseEnter = () => {
    if (document.body.getAttribute("data-disable-table-tooltip") === "true") {
      // console.log("Tooltip disabled due to open menu");
      return;
    }
    const rect = cellRef.current.getBoundingClientRect();
    setPosition({
      top: rect.top + window.scrollY + rect.height,
      left: rect.left,
    });
    hoverTimeoutRef.current = setTimeout(() => setShowPortal(true), 500);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = null;
    setShowPortal(false);
  };

  const handleMouseClick = () => {
    clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = null;
    setShowPortal(false);
  };

  const portalContent =
    newRecord[accessor] !== null &&
    newRecord[accessor] !== undefined &&
    newRecord[accessor] !== "--" &&
    typeof newRecord[accessor] !== "number" &&
    typeof newRecord[accessor] !== "boolean" &&
    showPortal &&
    tooltipConfig.enabled &&
    ReactDOM.createPortal(
      <div
        style={{
          position: "absolute",
          top: position.top,
          left: position.left,
          color: "white",
          background: "black",
          border: "1px solid white",
          padding: "5px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
          borderRadius: "5px",
          maxWidth: "200px",
          zIndex: 9999,
          fontSize: "12px",
        }}
      >
        {newRecord[accessor]}
      </div>,
      document.body,
    );

  const cellContent =
    typeof newRecord[accessor] === "number"
      ? newRecord[accessor]
      : (newRecord[accessor] || "--").toString();

  const tdClampStyle = (lines) => ({
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: lines,
    WebkitBoxOrient: "vertical",
    whiteSpace: "normal",
    wordBreak: "break-word",
  });

  if (WrapperComponent) {
    return (
      <div
        ref={cellRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={tooltipConfig.hideOnClick ? handleMouseClick : null}
      >
        <WrapperComponent record={newRecord} {...rest}>
          <span
            style={tdClampStyle(lines || 1)}
            title={
              !tooltipConfig.enabled && !tooltipConfig.disabledByColumn
                ? cellContent
                : null
            }
          >
            {cellContent}
          </span>
        </WrapperComponent>
        {portalContent}
      </div>
    );
  }

  if (UIComponent) {
    return (
      <div
        ref={cellRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={tooltipConfig.hideOnClick ? handleMouseClick : null}
      >
        <UIComponent record={newRecord} {...rest} />
        {portalContent}
      </div>
    );
  }

  return (
    <div
      ref={cellRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={tooltipConfig.hideOnClick ? handleMouseClick : null}
    >
      <span
        style={tdClampStyle(lines || 1)}
        title={
          !tooltipConfig.enabled && !tooltipConfig.disabledByColumn
            ? cellContent
            : null
        }
      >
        {dataType ? (
          <ShowAndEditContent
            content={cellContent}
            dataType={dataType}
            accessor={accessor}
            entityId={record.customFieldEntityId}
          />
        ) : (
          <div>{cellContent}</div>
        )}
      </span>
      {!dataType && portalContent}
    </div>
  );
};

export default RenderRecordsCell;
